"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.makeExperimentFlags = exports.default = exports.createExperimentFlags = void 0;
var _experiments = require("../experiments");
var _environment = _interopRequireDefault(require("./environment"));
var _storage = require("./storage");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * If experiment logic to be determined server-side, use server/src/experiments.js
 */

var NOT_IN_TEST = _experiments.experimentStates.NOT_IN_TEST,
  CONTROL = _experiments.experimentStates.CONTROL;
/**
 * makes the environment-based defaults for a flag
 */
var createExperimentFlags = exports.createExperimentFlags = function createExperimentFlags() {
  var development = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : NOT_IN_TEST;
  var staging = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : NOT_IN_TEST;
  var production = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : NOT_IN_TEST;
  return {
    development: development,
    staging: staging,
    production: production
  };
};
/**
 * Given an list of fallback variations for the flag, provide functions for
 * determining if a user is an experiment, in an A group, or in a B group.
 *
 * For simple A/B testing, where a user who is not in the test, is treated the
 * same as the control, you will only need to use `isUnderTest`.
 *
 * @param fallbackVariations
 * @example - A/B testing with the control and not-in-test treated the same
 *
 * // in your web-app-specific helper, define the flags.
 * // note, that if the default fallback for the flags are all NOT_IN_TEST, you can actually ommit
 * // the FALLBACK_VARIATIONS
 * // -- web-app/util/experimentFlags.js --
 * const FALLBACK_VARIATIONS = {
 * 'stanford-prison-experiment': experimentFlagsHelper.createExperimentFlags(NOT_IN_TEST, NOT_IN_TEST, NOT_IN_TEST),
 * };
 * export default experimentFlagsHelper.makeExperimentFlags(FALLBACK_VARIATIONS);
 *
 * // When you use the flags (in your web-app)
 * import { isUnderTest } from '~/util/experimentFlags`;
 * ...
 * const isUnderTest = experimentFlags.isUnderTest('stanford-prison-experiment', userContext);
 *
 * if (isUnderTest) {
 * ... user is in the B test (they are not control and they are in the experiment)
 * } else {
 * ... user is either the control or not in the test
 * }
 */
var makeExperimentFlags = exports.makeExperimentFlags = function makeExperimentFlags(fallbackVariations) {
  var getFallbackVariation = function getFallbackVariation(flagOrExperimentName) {
    if (fallbackVariations[flagOrExperimentName]) {
      return fallbackVariations[flagOrExperimentName][(0, _environment.default)()];
    }
    return NOT_IN_TEST;
  };

  /**
   * @param {string} experimentName
   *
   * returns override variation for a given experimentName, if set in localStorage
   * returns undefined, if no override set
   * @returns {ExperimentFlagsType | undefined}
   *
   * Note: never calls Custom Experiment Viewed event
   */
  var getOverrideVariation = function getOverrideVariation(experimentName) {
    var flagOverrides = (0, _experiments.getExperimentOverrides)();
    return flagOverrides[experimentName];
  };

  /**
   * Returns the variation for the experiment / user / environment
   *
   * @example
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'NOT_IN_TEST' // the user is not participating in the test
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'TEST-0'      // the user is in the control group
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'TEST-1'      // the user is in the first test group
   * getExperimentVariation('WEAR_A_MASK', userContext) => 'TEST-7'      // the user is in the seventh test group
   *
   * returns 'NOT_IN_TEST' if the user is not part of the experiment
   * returns 'TEST-0' if the user is in the control group
   * returns 'TEST-<N>' if the user is one of the test groups
   * @returns {string}
   */
  var getExperimentVariation = function getExperimentVariation(experimentName, userContext) {
    var triggerExperimentViewed = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var fallbackValue = getFallbackVariation(experimentName);
    if ((0, _storage.getCookie)('automatedTest')) {
      return NOT_IN_TEST;
    }

    // Return override only on dev & stage
    var isProduction = (0, _environment.default)() === 'production';
    var overrideVariation = getOverrideVariation(experimentName);
    if (!isProduction && overrideVariation) {
      return overrideVariation;
    }
    return (0, _experiments.getUserExperimentVariation)({
      experimentName: experimentName,
      userContext: userContext,
      fallbackValue: fallbackValue,
      triggerExperimentViewed: triggerExperimentViewed
    });
  };
  var getFlag = function getFlag(experimentName, userContext) {
    return getExperimentVariation(experimentName, userContext, false) !== NOT_IN_TEST;
  };

  /**
   * Is the user in the experiement or not.  Being in the experiment you
   * are either control or a variant.  The alternative is that you aren't under
   * test.  If an experiement is setup as 100% in, 50% for control (a), 50% for
   * varient (b) then everybody is in the A/B test (isInExperiment is always true)
   */
  var isInExperiment = function isInExperiment(experimentKey, userContext) {
    var triggerExperimentViewed = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var experimentVariation = getExperimentVariation(experimentKey, userContext, triggerExperimentViewed);
    return experimentVariation !== NOT_IN_TEST;
  };

  /**
   * Is the user in the experiment and are they the control group
   */
  var isControl = function isControl(experimentKey, userContext) {
    var triggerExperimentViewed = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    return getExperimentVariation(experimentKey, userContext, triggerExperimentViewed) === CONTROL;
  };

  /**
   * Is the in the experiment and are they _not_ the control group.
   * If you have more than one variant (ie, and A/B/C test) instead
   * of a single variant (classic A/B test) you will need to check
   * the getExperimentVariation directly as you would have values such
   * as NOT_IN_TEST, TEST-0, TEST-1, and TEST-2.
   */
  var isUnderTest = function isUnderTest(experimentKey, userContext) {
    var triggerExperimentViewed = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var variant = getExperimentVariation(experimentKey, userContext, triggerExperimentViewed);
    return variant !== CONTROL && variant !== NOT_IN_TEST;
  };
  return {
    getFlag: getFlag,
    getExperimentVariation: getExperimentVariation,
    isInExperiment: isInExperiment,
    isControl: isControl,
    isUnderTest: isUnderTest
  };
};
var _default = exports.default = {
  makeExperimentFlags: makeExperimentFlags,
  createExperimentFlags: createExperimentFlags,
  experimentStates: _experiments.experimentStates
};